import React from "react";
import { applyAnchorPositioningPolyfill } from "~/lib/anchorPositioningPolyfill";
import "~/css/tooltip.css";

type TooltipProps = {
  tooltip: React.ReactNode;
  placement?: "top" | "bottom";
  disabled?: boolean;
  children: React.ReactElement;
};

export function Tooltip({ tooltip, placement = "top", disabled = false, children }: TooltipProps) {
  const anchorName = `--tooltip-${React.useId().replace(/:/g, "")}`;
  const tooltipRef = React.useRef<HTMLSpanElement>(null);
  const delayRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = React.useCallback(() => {
    if (disabled) {
      return;
    }
    delayRef.current = setTimeout(() => {
      (tooltipRef.current as HTMLElement & { showPopover?: () => void })?.showPopover?.();
    }, 300);
  }, [disabled]);

  const hide = React.useCallback(() => {
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
    (tooltipRef.current as HTMLElement & { hidePopover?: () => void })?.hidePopover?.();
  }, []);

  React.useEffect(() => {
    if (disabled) {
      hide();
    }
  }, [disabled, hide]);

  React.useEffect(() => {
    if (tooltipRef.current) {
      applyAnchorPositioningPolyfill([tooltipRef.current]);
    }
    return () => {
      if (delayRef.current) {
        clearTimeout(delayRef.current);
      }
    };
  }, []);

  const trigger = React.cloneElement(children, {
    onMouseEnter: show,
    onMouseLeave: hide,
    style: { ...(children.props.style ?? {}), anchorName } as React.CSSProperties,
  });

  return (
    <>
      {trigger}
      <span
        ref={tooltipRef}
        popover="hint"
        role="tooltip"
        className="tooltip"
        data-placement={placement}
        style={{ positionAnchor: anchorName } as React.CSSProperties}
      >
        {tooltip}
      </span>
    </>
  );
}
